import React, { Suspense } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Trash2Outline } from '@lyearn/icons';
import { AddBlock } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Input, NumberInput, Stack, Typography } from '@lyearn/ui';

import { AdvancedMenuItemComponentProps } from '../../../../types/menus';
import { SECONDS_IN_HOUR, SECONDS_IN_MINUTES, useTimeStamps } from './hooks/useTimeStamps';
import { inputClasses, textAlignCetnerInput } from './styles';

const Timestamps: React.FC<AdvancedMenuItemComponentProps> = (props) => {
  const { t } = useTranslation();

  const {
    sections,
    onAddSection,
    onDeleteSection,
    onLabelChange,
    onTimeChange,
    onTimeChangeSort,
    showHourInput,
  } = useTimeStamps(props);

  return (
    <Suspense fallback={null}>
      <Stack direction="col">
        <Typography variant="label-02">{t('Lygo:AdvancedMenu.video.Sections.Title')}</Typography>
        <Typography className="mt-4 text-text-secondary" variant="body-short-03">
          {t('Lygo:AdvancedMenu.video.Sections.Description')}
        </Typography>
        {sections.length ? (
          <Stack className="mt-24" direction="col" gapY={12}>
            {sections.map((section, index: number) => {
              const hoursInSection = Math.floor(section.time / SECONDS_IN_HOUR);
              const minutesInSection = Math.floor(
                (section.time % SECONDS_IN_HOUR) / SECONDS_IN_MINUTES,
              );
              const secondsInSection = Math.floor(
                (section.time % SECONDS_IN_HOUR) % SECONDS_IN_MINUTES,
              );
              return (
                <Stack
                  key={index}
                  alignItems="center"
                  className="border border-field rounded-8 pl-12 pr-10 py-8 focus-within:border-interactive-primary group h-40"
                  gapX={6}>
                  {showHourInput ? (
                    <>
                      <NumberInput
                        className={cn(
                          'border-none max-w-[2rem]',
                          inputClasses,
                          textAlignCetnerInput,
                        )}
                        disabled={index === 0}
                        min={0}
                        showControls={false}
                        value={hoursInSection}
                        onBlur={onTimeChangeSort}
                        onChange={(value) =>
                          onTimeChange(
                            index,
                            minutesInSection * SECONDS_IN_MINUTES +
                              secondsInSection +
                              value * SECONDS_IN_HOUR,
                          )
                        }
                      />
                      <Typography variant="body-long-01">{':'}</Typography>
                    </>
                  ) : null}
                  <NumberInput
                    className={cn('border-none max-w-[2rem]', inputClasses, textAlignCetnerInput)}
                    disabled={index === 0}
                    max={59}
                    min={0}
                    showControls={false}
                    value={minutesInSection}
                    onBlur={onTimeChangeSort}
                    onChange={(value) =>
                      onTimeChange(
                        index,
                        hoursInSection * SECONDS_IN_HOUR +
                          secondsInSection +
                          value * SECONDS_IN_MINUTES,
                      )
                    }
                  />
                  <Typography variant="body-long-01">{':'}</Typography>
                  <NumberInput
                    className={cn('border-none max-w-[2rem]', inputClasses, textAlignCetnerInput)}
                    disabled={index === 0}
                    max={59}
                    min={0}
                    showControls={false}
                    value={secondsInSection}
                    onBlur={onTimeChangeSort}
                    onChange={(value) =>
                      onTimeChange(
                        index,
                        hoursInSection * SECONDS_IN_HOUR +
                          minutesInSection * SECONDS_IN_MINUTES +
                          value,
                      )
                    }
                  />
                  <Stack className="border-border border-l h-full" />
                  <Input
                    className={cn('border-none', inputClasses)}
                    placeholder={t('Lygo:AdvancedMenu.video.Sections.InputPlaceholder')}
                    value={section.label}
                    onChangeText={(nextValue) => onLabelChange(index, nextValue)}
                  />
                  {index !== 0 ? (
                    <Stack
                      className="cursor-pointer opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
                      onClick={() => onDeleteSection(index)}>
                      <Trash2Outline className="h-20 w-20" fillColorToken="link-internal" />
                    </Stack>
                  ) : null}
                </Stack>
              );
            })}
          </Stack>
        ) : null}
        <AddBlock
          className={sections?.length ? 'mt-12' : 'mt-16'}
          title={t('Lygo:AdvancedMenu.video.Sections.Button')}
          onClick={onAddSection}
        />
      </Stack>
    </Suspense>
  );
};

export default Timestamps;
